import {messages} from '@smartmixin/i18n';
import type {MessageDescriptor} from 'react-intl';
import {DataGranularity} from '../../../api/types';
import * as Units from '../../Units';
import {DateTimeConfig} from '../../Dates';
import {
  createXAxisConfig,
  createYAxisConfig,
  createTooltipConfig,
  createGridConfig,
  createLegendConfig,
} from '../builders';
import {ChartTheme} from '../theme';
import {ChartConfig, ChartDataPoint, SeriesConfig} from '../types';

export interface HumidityChartOptions {
  data: ChartDataPoint[];
  theme: ChartTheme;
  timezone: string;
  units: Units.Units;
  granularity: DataGranularity;
  height?: number;
  comparisonData?: ChartDataPoint[];
  comparisonLabel?: string;
  dateTimeConfig?: DateTimeConfig;
  /** When provided, uses category x-axis instead of datetime (for analysis charts) */
  categories?: string[];
  /**
   * Names the series in the user's language. Required: the names are
   * messages, and English would otherwise reach every locale.
   */
  formatMessage: (descriptor: MessageDescriptor) => string;
}

export const humidityChart = (options: HumidityChartOptions): ChartConfig => {
  const {
    data,
    theme,
    timezone,
    units,
    granularity,
    height = 250,
    comparisonData,
    comparisonLabel,
    dateTimeConfig,
    categories,
    formatMessage,
  } = options;

  const hasRange = ['D', 'W', 'M'].includes(granularity);

  const series: SeriesConfig[] = [
    {
      id: 'humidity',
      type: 'line',
      dataKey: 'U',
      name: formatMessage(messages.metrics.humidity),
      color: theme.colors.primary,
      yAxisIndex: 0,
      interpolation: 'monotone',
      lineWidth: 2,
      showMarkers: false,
      zIndex: 2,
    },
  ];

  if (hasRange) {
    series.push({
      id: 'humidity-range',
      type: 'areaRange',
      dataKey: 'U_RANGE',
      rangeKey: 'U_RANGE',
      name: formatMessage(messages.charts.minMaxRange),
      color: theme.colors.primary,
      yAxisIndex: 0,
      interpolation: 'monotone',
      fillOpacity: 0.2,
      lineWidth: 0,
      showMarkers: false,
      zIndex: 0,
    });
  }

  if (comparisonData) {
    series.push({
      id: 'humidity-comparison',
      type: 'line',
      dataKey: 'U_COMP',
      name: comparisonLabel ?? formatMessage(messages.charts.comparison),
      color: theme.colors.textSecondary,
      yAxisIndex: 0,
      interpolation: 'monotone',
      lineWidth: 2,
      dashArray: '5 3',
      showMarkers: false,
      zIndex: 1,
    });
  }

  return {
    height,
    data,
    series,
    xAxis: createXAxisConfig({
      dateTimeConfig,
      type: categories ? 'category' : 'datetime',
      timezone,
      granularity,
      theme,
      categories,
    }),
    yAxes: [
      createYAxisConfig({
        id: 'humidity',
        metric: 'U',
        units,
        theme,
        clampMax: 100,
      }),
    ],
    tooltip: createTooltipConfig({
      timezone,
      granularity,
      metric: 'U',
      units,
      theme,
      dateTimeConfig,
      tooltipLabels: categories,
    }),
    legend: createLegendConfig(series.length > 2, theme),
    grid: createGridConfig(theme),
    animate: true,
  };
};
